/**
 * Provider Evidence Honesty (PRE) — small, pure helpers shared between
 *   the SED producer (`services/siteEvidence.ts`) and the renderer
 *   (`SEDVariableBrowser.tsx`).
 *
 * Background (PRE pack — row 0.99251, 2026-05-18). Live-prod customer
 * feedback flagged the dishonest provider chip:
 *
 *   Elevation (Min) —m · User Drawn Boundary (Google Maps Drawing +
 *   Elevation API)
 *
 * where every elevation value was null even though the section claimed
 * `Google Maps Elevation API` as a source. PRE separates the
 * "we attempted X" channel from the "X gave us a value" channel so
 * the empty case stays honest:
 *
 *   - value present + source        → confident chip (existing)
 *   - value empty + attemptedProvider → "⚠ Provider — reason" chip
 *   - value empty + no attempt        → "— not collected" chip
 *
 * The schema lives on `EvidenceSection` (carrier) + `SEDVariable`
 * (projection). This file holds the cross-cutting helpers — backoff,
 * reason copy, age formatting — so every consumer reads the same
 * vocabulary.
 *
 * Pure (no I/O, no React). All units are wall-clock milliseconds /
 * ISO strings — same convention as the rest of the codebase.
 */

import type { EvidenceSection } from '../types/siteEvidence';

/** PRE attempt-result enum, mirrored from `EvidenceSection.attemptResult`. */
export type AttemptResult = NonNullable<EvidenceSection<unknown>['attemptResult']>;

/**
 * Wall-clock backoff for the next auto-retry attempt. Returns an ISO
 * timestamp `attempt`-units in the future, or `null` when no retry is
 * scheduled.
 *
 * Pack-prescribed values (verbatim):
 *   - rate-limited → +1h
 *   - http-error   → +30min     (5xx / 4xx)
 *   - timeout      → +15min
 *   - empty        → +24h       (legit empty — don't churn)
 *   - everything else → null    (no auto-retry)
 *
 * The `attempt` parameter is reserved for a future exponential-backoff
 * pass (PRE-FOLLOWUP) — the linear schedule above is enough for the
 * hotfix and avoids the rate-limit-on-retry tail risk called out in R2.
 *
 * `now` is injectable so the caller can pin the clock during tests.
 */
export function nextRetryFromBackoff(
  result: AttemptResult,
  attempt: number,
  now: number = Date.now(),
): string | null {
  void attempt; // reserved for future exponential pass
  const MIN = 60_000;
  const HOUR = 60 * MIN;
  switch (result) {
    case 'rate-limited':
      return new Date(now + 1 * HOUR).toISOString();
    case 'http-error':
      return new Date(now + 30 * MIN).toISOString();
    case 'timeout':
      return new Date(now + 15 * MIN).toISOString();
    case 'empty':
      return new Date(now + 24 * HOUR).toISOString();
    case 'success':
    case 'not-attempted':
    default:
      return null;
  }
}

/**
 * Short human-readable label for an `AttemptResult`. Used by the
 * "⚠ Provider — {reason}" chip when the producer didn't supply a more
 * specific `emptyReason`.
 *
 * Kept short (≤25 chars) so the chip fits inline with the provider
 * name without truncation.
 */
export function defaultReasonFor(result: AttemptResult | undefined | null): string {
  switch (result) {
    case 'empty':
      return 'No data returned';
    case 'http-error':
      return 'Provider error';
    case 'timeout':
      return 'Provider timed out';
    case 'rate-limited':
      return 'Provider rate-limited';
    case 'not-attempted':
      return 'Not attempted';
    case 'success':
      return 'Success';
    default:
      return 'Data unavailable';
  }
}

/**
 * "N min/hr/d ago" formatter for the `lastAttemptedAt` ISO string.
 * Returns the empty string when the input is missing or unparseable —
 * the renderer just omits the suffix in that case (no "Last attempted
 * unknown" noise).
 *
 * Sub-minute resolution is intentionally rounded up to "just now"
 * — the user doesn't need second-precision on a provider attempt.
 *
 * @param iso  ISO-8601 timestamp of the attempt.
 * @param now  Wall-clock now in ms (injectable for tests).
 */
export function ago(iso: string | undefined | null, now: number = Date.now()): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const ms = now - t;
  if (ms < 0) return 'just now';
  const SEC = 1_000;
  const MIN = 60 * SEC;
  const HOUR = 60 * MIN;
  const DAY = 24 * HOUR;
  if (ms < MIN) return 'just now';
  if (ms < HOUR) return `${Math.floor(ms / MIN)}m ago`;
  if (ms < DAY) return `${Math.floor(ms / HOUR)}h ago`;
  return `${Math.floor(ms / DAY)}d ago`;
}

/**
 * Discriminator: does the slot's `(value, provenance)` pair carry the
 * "provider attempted but empty" signature? The renderer uses this to
 * pick the "⚠ Provider — reason" chip over the bare provider label.
 *
 * The legacy clobber-fingerprint (value === null but `attemptedProvider`
 * unset because the slot pre-dates PRE-1) is handled by callers that
 * want lazy migration — see the pack's R1 mitigation. This helper only
 * answers the explicit PRE state.
 */
export function isAttemptedEmpty(
  value: unknown,
  attemptedProvider: string | undefined | null,
): boolean {
  if (!attemptedProvider) return false;
  return value == null;
}

// ──────────────────────────────────────────────────────────────────────
//  PRE-5 — Group-header aggregate chip (2026-05-19)
//
//  Live-prod symptom on the Wave-7 Sickel SED Variable Browser: every
//  domain group header rendered a uniform `VERIFIED · {N} verified`
//  chip because the legacy aggregator was "row exists in SED → counts
//  as verified" and did NOT consult `provenance.attemptResult`. So
//  `siteProfile` rendered `VERIFIED · 7 verified` even when every
//  elevation slot carried `attemptResult: 'empty'`.
//
//  `aggregateGroupState` rolls up the per-slot PRE state into a
//  state-distinguishing chip, with a clean back-compat path for legacy
//  slots that pre-date PRE-1 (no `attemptResult` field): they default
//  to the existing "verified" tier when the slot carries a value, or
//  `'not-attempted'` when the slot is empty AND has no attemptedProvider
//  (matches the renderer's "— not collected" branch).
//
//  Composes with EAS — once the elevation cascade lands the Stage-4
//  USGS fallback, slots that previously rendered `empty` after the
//  vertex stage flip to `success`; the chip then flips from
//  `PARTIALLY VERIFIED` to `VERIFIED` without any further wiring.
// ──────────────────────────────────────────────────────────────────────

/**
 * Tone vocabulary for the PRE-5 group chip. Maps onto the existing
 * Tailwind tokens used by the SET-Q2 worst-status chip — `success` is
 * the green VERIFIED treatment, `amber` is the warning treatment,
 * `red` is the danger treatment, `muted` is the silent-grey treatment
 * for "not collected yet". Kept abstract here so renderers in different
 * surfaces (variable browser, section editor, future report cover) can
 * pick the right class names without re-deriving the rules.
 */
export type GroupChipTone = 'success' | 'amber' | 'red' | 'muted';

/**
 * PRE-5 chip label vocabulary. Five states beat one, in priority order
 * (most informative → least informative):
 *
 *  - `VERIFIED`           — every slot returned a usable value.
 *  - `PARTIALLY VERIFIED` — some slots succeeded, some failed/pending.
 *  - `NO DATA`            — every attempted slot came back empty.
 *  - `NOT COLLECTED`      — no slots attempted yet (e.g. coverage
 *                            gate skipped the section).
 *  - `MIXED`              — defensive fallback for count combinations
 *                            that don't cleanly map to the four primary
 *                            states.
 */
export type GroupChipLabel =
  | 'VERIFIED'
  | 'PARTIALLY VERIFIED'
  | 'NO DATA'
  | 'NOT COLLECTED'
  | 'MIXED';

/**
 * PRE-5 aggregate chip projection. Pure data — renderers pick the
 * className from `tone` and surface `subtitle` as the second-line
 * breakdown text.
 *
 *  - `label`     — the chip's display text (uppercase, ≤20 chars).
 *  - `tone`      — colour treatment.
 *  - `subtitle`  — sentence-cased breakdown (e.g. "5/8 verified ·
 *                  3 attempted-empty"). Always populated so the chip
 *                  caller doesn't have to special-case the empty case.
 *  - `counts`    — raw per-bucket counts so renderers / tests can
 *                  inspect the aggregation without re-running it.
 */
export interface GroupChipState {
  readonly label: GroupChipLabel;
  readonly tone: GroupChipTone;
  readonly subtitle: string;
  readonly counts: {
    readonly total: number;
    readonly success: number;
    readonly empty: number;
    readonly notAttempted: number;
  };
}

/**
 * Minimal structural shape `aggregateGroupState` reads off each slot.
 * Intentionally a structural projection rather than a hard-import of
 * `SEDVariable` so the helper can be reused by the section-level
 * surfaces (each variable / each EvidenceSection) without dragging in
 * the variable-browser type tree. Callers project their domain shape
 * into this minimal record.
 *
 *  - `value`          — the slot's effective value. `null` / `undefined`
 *                       means empty.
 *  - `attemptResult`  — the PRE-1 channel. When absent, the helper
 *                       infers `'success'` for non-null `value` and
 *                       `'not-attempted'` for null `value` (so legacy
 *                       slots without the PRE schema map cleanly onto
 *                       the existing "row exists → verified" semantics).
 */
export interface AggregatableSlot {
  readonly value: unknown;
  readonly attemptResult?: AttemptResult | null;
}

/**
 * Classify a single slot's PRE state. Lifted out so the aggregator and
 * tests can share the per-slot view — the legacy fall-through (no
 * `attemptResult`) is the trickiest bit and we want one source of truth
 * for it.
 *
 * Decision order:
 *  1. `value != null` AND no explicit non-success `attemptResult` →
 *     `success`. Covers BOTH the legacy verified-tier row (no
 *     `attemptResult` at all, value present) AND the partial-success
 *     section case where the producer stamped a section-level
 *     `attemptResult: 'empty'` because the section's MAIN provider
 *     payload was empty but a sibling field still carries a value
 *     (Sickel `siteProfile`: `acres = 120` is real, the elevation
 *     fields are null — the per-row chip is gated on null `value`, so
 *     the aggregator gates on the same).
 *  2. `value != null` AND explicit `attemptResult: 'success'` → success
 *     (full agreement).
 *  3. `value == null` AND explicit `attemptResult` → use it
 *     (`empty` / `http-error` / `timeout` / `rate-limited` /
 *     `not-attempted` / even `'success'` defensively — a null value
 *     with a `success` flag is an inconsistency the renderer downgrades
 *     to `empty` to keep the chip honest).
 *  4. `value == null` AND no `attemptResult` → `not-attempted` (legacy
 *     `status: 'unavailable'` row OR pre-PRE-1 empty slot).
 *
 * The four error sub-states (`empty` / `http-error` / `timeout` /
 * `rate-limited`) collapse into the `empty` bucket from the
 * aggregator's point of view — the pack's chip vocabulary only
 * distinguishes "attempted-empty" vs "not-attempted" vs "success",
 * and the per-slot chip (PRE-2) is the surface that surfaces the
 * sub-state to the user.
 */
function classifySlot(slot: AggregatableSlot): AttemptResult {
  const explicit = slot.attemptResult ?? undefined;
  // Value-present wins: a slot with real data is success regardless of
  // the section-level attempt-result (which speaks for the section's
  // main payload, not the per-field projection). Mirrors the per-row
  // PRE-2 chip gate (`isAttemptedEmpty` requires value == null).
  if (slot.value != null) return 'success';
  // Null value: trust the explicit attempt-result when it exists.
  if (explicit) {
    // Defensive guard against the inconsistent (value==null,
    // result==success) pair — surface it as attempted-empty so the
    // chip's breakdown stays truthful instead of fabricating success.
    if (explicit === 'success') return 'empty';
    return explicit;
  }
  // Null value, no attempt recorded: legacy / pre-PRE-1 empty slot.
  return 'not-attempted';
}

/**
 * PRE-5 — Roll up per-slot `attemptResult` into a state-distinguishing
 * group chip. Pure projection — no I/O, no React, no clock reads —
 * suitable for the renderer to call inline on every mount.
 *
 * Decision rules (verbatim from the pack, normalised to handle the
 * `empty === 0 && notAttempted === 0` edge case the pack's first two
 * branches share):
 *
 *  1. `total === 0`               → MIXED muted (defensive — empty
 *                                    groups shouldn't surface but a
 *                                    fallback keeps the chip honest).
 *  2. success === total           → VERIFIED green.
 *  3. success > 0 ∧ empty > 0     → PARTIALLY VERIFIED amber, subtitle
 *                                    "${success}/${total} verified ·
 *                                    ${empty} attempted-empty".
 *  4. success > 0 ∧ notAttempted > 0 → PARTIALLY VERIFIED amber,
 *                                    subtitle "${success}/${total}
 *                                    verified · ${notAttempted} pending".
 *  5. success === 0 ∧ empty > 0   → NO DATA red, subtitle "0/${total}
 *                                    verified · ${empty} attempted-empty".
 *  6. success === 0 ∧ notAttempted === total → NOT COLLECTED muted.
 *  7. otherwise                   → MIXED amber, subtitle "${success}/
 *                                    ${total} verified" (rare — usually
 *                                    means success + both other buckets
 *                                    populated; surfaces honestly).
 */
export function aggregateGroupState(slots: ReadonlyArray<AggregatableSlot>): GroupChipState {
  const total = slots.length;

  let success = 0;
  let empty = 0;
  let notAttempted = 0;

  for (const slot of slots) {
    const r = classifySlot(slot);
    if (r === 'success') {
      success += 1;
    } else if (r === 'not-attempted') {
      notAttempted += 1;
    } else {
      // empty / http-error / timeout / rate-limited all collapse into
      // "attempted-empty" from the aggregator's POV — the per-slot chip
      // (PRE-2) surfaces the failure-mode sub-state separately.
      empty += 1;
    }
  }

  const counts = { total, success, empty, notAttempted } as const;

  if (total === 0) {
    return { label: 'MIXED', tone: 'muted', subtitle: '0/0 verified', counts };
  }

  if (success === total) {
    return {
      label: 'VERIFIED',
      tone: 'success',
      subtitle: `${success} verified`,
      counts,
    };
  }

  if (success > 0 && empty > 0 && notAttempted === 0) {
    return {
      label: 'PARTIALLY VERIFIED',
      tone: 'amber',
      subtitle: `${success}/${total} verified · ${empty} attempted-empty`,
      counts,
    };
  }

  if (success > 0 && notAttempted > 0 && empty === 0) {
    return {
      label: 'PARTIALLY VERIFIED',
      tone: 'amber',
      subtitle: `${success}/${total} verified · ${notAttempted} pending`,
      counts,
    };
  }

  if (success === 0 && empty > 0 && notAttempted === 0) {
    return {
      label: 'NO DATA',
      tone: 'red',
      subtitle: `0/${total} verified · ${empty} attempted-empty`,
      counts,
    };
  }

  if (success === 0 && notAttempted === total) {
    return {
      label: 'NOT COLLECTED',
      tone: 'muted',
      subtitle: `0/${total} attempted`,
      counts,
    };
  }

  // Defensive fallback for all other count combinations (success +
  // empty + notAttempted all > 0, or success === 0 with both empty AND
  // notAttempted). The subtitle keeps the breakdown honest so a user
  // looking at MIXED can still see why.
  const breakdown: string[] = [`${success}/${total} verified`];
  if (empty > 0) breakdown.push(`${empty} attempted-empty`);
  if (notAttempted > 0) breakdown.push(`${notAttempted} pending`);
  return {
    label: 'MIXED',
    tone: 'amber',
    subtitle: breakdown.join(' · '),
    counts,
  };
}

/**
 * Canonical provider id → display label table for admin / report surfaces.
 *
 * The SED renderer (`SEDVariableBrowser`) carries its own private inline
 * `humanizeProvider` so its chip vocabulary can drift independently —
 * that's a deliberate engineering decision baked in by the PRE pack
 * (each renderer owns its chip copy).
 *
 * The admin Provider Posture surface (PP pack) is the third consumer and
 * needs a SUPERSET — it has to label every provider id that may surface
 * in a `'provider.empty'` or `'external-literature.refreshed'` audit
 * event, including the live-literature trio (Crossref / arXiv / PubMed)
 * which the renderers never show. Centralising here keeps that surface
 * honest without disturbing the inline copies.
 *
 * Unknown ids fall back to the raw string so a new provider that has not
 * been registered here still renders meaningfully in the admin table.
 * Add an entry whenever a new provider id starts emitting audit events.
 */
const PROVIDER_LABELS: Readonly<Record<string, string>> = {
  // siteProfile / elevation cascade
  'google-maps-elevation': 'Google Maps Elevation API',
  'usgs-epqs':             'USGS EPQS',
  'site-profile':          'Site Profile',
  // live-literature orchestrator (LLW)
  'external-literature':   'Live Literature (orchestrator)',
  'crossref':              'Crossref',
  'arxiv':                 'arXiv',
  'pubmed':                'PubMed',
};

/**
 * Provider id → display label. Used by the admin Provider Posture page +
 * the rerun Provider Posture row. Renderer surfaces keep their own
 * inline copies — see `PROVIDER_LABELS` for the rationale.
 */
export function humanizeProvider(id: string | undefined | null): string {
  if (!id) return 'Unknown provider';
  return PROVIDER_LABELS[id] ?? id;
}
